import { BaseController } from '../../../base_controller';

const UNIQUE_ID = 'votes--dashboard--calendar-component';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLOR_VAR: Record<string, string> = {
  Setup: '--cal-st-setup',
  Payment: '--cal-st-payment',
  Ready: '--cal-st-ready',
  Running: '--cal-st-running',
  'Verifying Payment': '--cal-st-verify',
  Ended: '--cal-st-ended',
};

const LEGEND_ORDER = [
  'Setup',
  'Payment',
  'Ready',
  'Running',
  'Verifying Payment',
  'Ended',
];

const DEFAULT_STATUS_COLOR_VAR = '--cal-st-setup';

const TYPE_LABEL: Record<string, string> = {
  election: 'ElectionVote',
  motion: 'MeetingVote',
  announcement: 'Announcement',
};

interface CalendarEvent {
  id: number;
  name: string;
  type_of: string;
  status: string;
  start_date: string;
  end_date: string;
  turnout: string;
}

interface DashboardGridRow {
  id: number | string;
  name: string;
  type_of: string;
  status_label: string | null;
  start_on: string | null;
  end_on: string | null;
  schedule_summary_html: string | null;
}

interface DashboardGridDataResponse {
  rows: DashboardGridRow[];
  lastRow: number;
}

const SSRM_GRID_ID = 'application--toolkit--ag-grid-ssrm-component--ag-grid-ssrm-component';

interface SsrmGridOutlet {
  switchToBasic(): void;
  switchToAdvanced(): void;
  resizeGrid(): void;
  setVoteBoxesHidden(hidden: boolean): void;
}

const VOTE_BOXES_PANEL_SELECTOR = '.votes--dashboard--vote-boxes-panel-component';

const VOTE_BOXES_PANEL_TOGGLE_SELECTOR = '.js-root-ag-votebox-panel-toggle';

const GRID_TOGGLE_ACTIVE_CLASS = 'ag-grid-view-toggle__btn--active';

interface DashboardGridApi {
  getFilterModel(): Record<string, unknown>;
  addEventListener(eventType: string, listener: () => void): void;
  removeEventListener(eventType: string, listener: () => void): void;
}

const MAX_EVENTS_FETCH = 300;

const MAX_LANES = 3;

interface WeekSegment {
  event: CalendarEvent;
  col: number;
  colEnd: number;
  lane: number;
  contL: boolean;
  contR: boolean;
  sortKey: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const stripTime = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const parseISODate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date): string =>
  `${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getDate()}, ${date.getFullYear()}`;

const formatMonthDay = (date: Date): string =>
  `${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getDate()}`;

const formatDateRange = (start: Date, end: Date): string => {
  if (sameDay(start, end)) return formatDate(start);
  if (start.getFullYear() === end.getFullYear()) {
    return `${formatMonthDay(start)} – ${formatDate(end)}`;
  }
  return `${formatDate(start)} – ${formatDate(end)}`;
};

export default class extends BaseController {
  static targets = [
    'panel',
    'monthTitle',
    'weekdays',
    'grid',
    'legend',
    'scheduledCount',
    'unscheduledNote',
    'detail',
    'detailName',
    'detailType',
    'detailStatus',
    'detailDates',
    'detailTurnout',
    'detailOpenLink',
    'dayAgenda',
    'dayAgendaTitle',
    'dayAgendaList',
  ];

  static values = {
    unscheduledCount: Number,
    planExpiry: String,
    dataUrl: String,
  };

  declare readonly panelTarget: HTMLElement;
  declare readonly monthTitleTarget: HTMLElement;
  declare readonly weekdaysTarget: HTMLElement;
  declare readonly gridTarget: HTMLElement;
  declare readonly legendTarget: HTMLElement;
  declare readonly scheduledCountTarget: HTMLElement;
  declare readonly unscheduledNoteTarget: HTMLElement;
  declare readonly detailTarget: HTMLElement;
  declare readonly detailNameTarget: HTMLElement;
  declare readonly detailTypeTarget: HTMLElement;
  declare readonly detailStatusTarget: HTMLElement;
  declare readonly detailDatesTarget: HTMLElement;
  declare readonly detailTurnoutTarget: HTMLElement;
  declare readonly detailOpenLinkTarget: HTMLAnchorElement;
  declare readonly dayAgendaTarget: HTMLElement;
  declare readonly dayAgendaTitleTarget: HTMLElement;
  declare readonly dayAgendaListTarget: HTMLElement;

  declare readonly unscheduledCountValue: number;
  declare readonly planExpiryValue: string;
  declare readonly dataUrlValue: string;

  private loadFailed = false;
  private cursor: Date = new Date();
  private today: Date = new Date();
  private calendarToggleButtons: HTMLElement[] = [];
  private gridToggleButtons: HTMLElement[] = [];
  private mobileToggleButtons: HTMLElement[] = [];
  private panelToggleButtons: HTMLElement[] = [];
  private boundToggleCalendar: () => void = () => this.toggleCalendar();
  private boundSelectBasic: () => void = () => this.selectGridView('basic');
  private boundSelectAdvanced: () => void = () => this.selectGridView('advanced');
  private boundSelectGridFromMobile: (event: Event) => void = (event) =>
    this.selectGridViewFromMobile(event);
  private boundToggleVoteBoxesPanel: () => void = () => this.toggleVoteBoxesPanel();

  private gridApi: DashboardGridApi | null = null;
  private gridReadyHandler: (event: Event) => void = (event) =>
    this.attachGridApi(
      ((event as CustomEvent<{ gridApi: unknown }>).detail
        ?.gridApi as DashboardGridApi) ?? null
    );
  private boundFilterChanged: () => void = () => {
    if (this.activeView === 'calendar') void this.renderMonth();
  };

  private voteBoxesPanelOn = false;

  private activeView: 'basic' | 'advanced' | 'calendar' = 'basic';
  private lastGridView: 'basic' | 'advanced' = 'basic';

  connect(): void {
    this.today = new Date();
    this.cursor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);

    this.buildWeekdayHeader();
    this.buildLegend();
    this.wireExternalToggles();
    this.adoptInitialGridView();
    document.addEventListener('votes:dashboard:grid-ready', this.gridReadyHandler);
    document.dispatchEvent(
      new CustomEvent('votes:dashboard:request-grid-api')
    );
    this.syncSegmentState();
    this.applyPanelVisibility();

    this.renderMonth();
  }

  disconnect(): void {
    this.calendarToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundToggleCalendar)
    );
    const [basicBtn, advancedBtn] = this.gridToggleButtons;
    basicBtn?.removeEventListener('click', this.boundSelectBasic);
    advancedBtn?.removeEventListener('click', this.boundSelectAdvanced);
    this.mobileToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundSelectGridFromMobile)
    );
    this.panelToggleButtons.forEach((btn) =>
      btn.removeEventListener('click', this.boundToggleVoteBoxesPanel)
    );
    document.removeEventListener(
      'votes:dashboard:grid-ready',
      this.gridReadyHandler
    );
    this.attachGridApi(null);
  }

  private attachGridApi(api: DashboardGridApi | null): void {
    if (this.gridApi === api) return;
    this.gridApi?.removeEventListener('filterChanged', this.boundFilterChanged);
    this.gridApi = api;
    this.gridApi?.addEventListener('filterChanged', this.boundFilterChanged);
  }

  private wireExternalToggles(): void {
    this.calendarToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.js-root-ag-calendar-toggle')
    );
    this.gridToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.ag-grid-view-toggle__btn')
    );
    this.mobileToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>('.ag-grid-view-toggle-mobile')
    );

    this.calendarToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundToggleCalendar)
    );

    const [basicBtn, advancedBtn] = this.gridToggleButtons;
    basicBtn?.addEventListener('click', this.boundSelectBasic);
    advancedBtn?.addEventListener('click', this.boundSelectAdvanced);

    this.mobileToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundSelectGridFromMobile)
    );

    this.panelToggleButtons = Array.from(
      document.querySelectorAll<HTMLElement>(VOTE_BOXES_PANEL_TOGGLE_SELECTOR)
    );
    this.panelToggleButtons.forEach((btn) =>
      btn.addEventListener('click', this.boundToggleVoteBoxesPanel)
    );
  }

  private adoptInitialGridView(): void {
    const [, advancedBtn] = this.gridToggleButtons;
    if (advancedBtn?.classList.contains(GRID_TOGGLE_ACTIVE_CLASS)) {
      this.activeView = 'advanced';
      this.lastGridView = 'advanced';
    }
  }

  toggleCalendar(): void {
    if (this.activeView === 'calendar') {
      this.selectGridView(this.lastGridView);
      return;
    }
    this.showCalendar();
  }

  showCalendar(): void {
    this.activeView = 'calendar';
    this.gridWrapper()?.classList.add(
      'root-ag-dashboard-grid--calendar-active'
    );
    this.panelTarget.hidden = false;
    void this.renderMonth();
    this.syncSegmentState();
    this.applyPanelVisibility();
  }

  showGrid(): void {
    this.selectGridView(this.lastGridView);
  }

  private selectGridView(view: 'basic' | 'advanced'): void {
    this.activeView = view;
    this.lastGridView = view;
    this.gridWrapper()?.classList.remove(
      'root-ag-dashboard-grid--calendar-active'
    );
    this.panelTarget.hidden = true;
    this.closeDetail();
    this.syncSegmentState();
    this.applyPanelVisibility();
    this.applyMasterDetailMode(view);
  }

  private applyMasterDetailMode(view: 'basic' | 'advanced'): void {
    const grid = this.ssrmGrid();
    if (!grid) return;

    if (view === 'basic') {
      grid.switchToBasic();
    } else {
      grid.switchToAdvanced();
    }
  }

  private ssrmGrid(): SsrmGridOutlet | null {
    const el = this.gridWrapper()?.querySelector<HTMLElement>(
      `[data-controller~="${SSRM_GRID_ID}"]`
    );
    if (!el) return null;

    return this.application.getControllerForElementAndIdentifier(
      el,
      SSRM_GRID_ID
    ) as unknown as SsrmGridOutlet | null;
  }

  private applyPanelVisibility(): void {
    if (this.activeView !== 'advanced') this.voteBoxesPanelOn = false;

    const panelVisible = this.activeView === 'advanced' && this.voteBoxesPanelOn;
    const panel = this.voteBoxesPanelElement();
    if (panel) {
      panel.hidden = !panelVisible;
    }
    this.syncPanelToggleButtons();
    this.ssrmGrid()?.setVoteBoxesHidden(panelVisible);
    this.resizeGrid();
  }

  private voteBoxesPanelElement(): HTMLElement | null {
    return (
      this.element
        .closest<HTMLElement>('.root-ag-dashboard-layout')
        ?.querySelector<HTMLElement>(VOTE_BOXES_PANEL_SELECTOR) ?? null
    );
  }

  private toggleVoteBoxesPanel(): void {
    if (this.activeView !== 'advanced') return;
    this.voteBoxesPanelOn = !this.voteBoxesPanelOn;
    this.applyPanelVisibility();
  }

  private syncPanelToggleButtons(): void {
    const isAdvanced = this.activeView === 'advanced';
    this.panelToggleButtons.forEach((btn) => {
      btn.hidden = !isAdvanced;
      btn.setAttribute('aria-pressed', this.voteBoxesPanelOn ? 'true' : 'false');
    });
  }

  private resizeGrid(): void {
    this.ssrmGrid()?.resizeGrid();
  }

  private selectGridViewFromMobile(event: Event): void {
    const btn = event.currentTarget as HTMLElement;
    const view: 'basic' | 'advanced' =
      btn.textContent?.trim() === 'Advanced' ? 'advanced' : 'basic';
    this.selectGridView(view);
  }

  private syncSegmentState(): void {
    this.calendarToggleButtons.forEach((btn) =>
      btn.setAttribute(
        'aria-pressed',
        this.activeView === 'calendar' ? 'true' : 'false'
      )
    );

    this.gridToggleButtons.forEach((btn, index) => {
      const view: 'basic' | 'advanced' = index === 0 ? 'basic' : 'advanced';
      btn.classList.toggle(GRID_TOGGLE_ACTIVE_CLASS, this.activeView === view);
    });
  }

  private gridWrapper(): HTMLElement | null {
    return this.element.closest<HTMLElement>('.root-ag-dashboard-grid');
  }

  prevMonth(): void {
    this.cursor = new Date(
      this.cursor.getFullYear(),
      this.cursor.getMonth() - 1,
      1
    );
    this.renderMonth();
  }

  nextMonth(): void {
    this.cursor = new Date(
      this.cursor.getFullYear(),
      this.cursor.getMonth() + 1,
      1
    );
    this.renderMonth();
  }

  goToToday(): void {
    this.cursor = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    this.renderMonth();
  }

  closeDetail(): void {
    this.detailTarget.removeAttribute('open');
  }

  backdropClick(event: MouseEvent): void {
    if (event.target === this.detailTarget) this.closeDetail();
  }

  private openDayAgenda(date: Date, events: CalendarEvent[]): void {
    this.dayAgendaTitleTarget.textContent = formatDate(date);

    this.dayAgendaListTarget.innerHTML = '';
    events.forEach((calendarEvent) => {
      this.dayAgendaListTarget.appendChild(this.buildAgendaRow(calendarEvent));
    });

    this.dayAgendaTarget.setAttribute('open', '');
  }

  closeDayAgenda(): void {
    this.dayAgendaTarget.removeAttribute('open');
  }

  dayAgendaBackdropClick(event: MouseEvent): void {
    if (event.target === this.dayAgendaTarget) this.closeDayAgenda();
  }

  private buildAgendaRow(calendarEvent: CalendarEvent): HTMLButtonElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = this.b('agenda-row');
    row.style.setProperty(
      '--cal-c',
      `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`
    );

    const dot = document.createElement('span');
    dot.className = this.b('agenda-dot');

    const name = document.createElement('span');
    name.className = this.b('agenda-name');
    name.textContent = calendarEvent.name;

    const arrow = document.createElement('span');
    arrow.className = this.b('agenda-arrow');
    arrow.textContent = '›';

    row.append(dot, name, arrow);

    row.addEventListener('click', () => {
      this.closeDayAgenda();
      this.openDetail(calendarEvent);
    });

    return row;
  }

  private planExpiryDate(): Date | null {
    if (!this.planExpiryValue) return null;
    return stripTime(parseISODate(this.planExpiryValue));
  }

  private isPlanExpiry(date: Date): boolean {
    const expiry = this.planExpiryDate();
    return expiry !== null && sameDay(date, expiry);
  }

  private async loadEvents(
    fromISO: string,
    toISO: string
  ): Promise<CalendarEvent[]> {
    this.loadFailed = false;
    if (!this.dataUrlValue) return [];

    try {
      const rows = await this.fetchGridDataRows(fromISO, toISO);

      return rows
        .filter((row) => !!row.start_on)
        .map((row) => this.mapRowToEvent(row));
    } catch (error) {
      this.loadFailed = true;
      // eslint-disable-next-line no-console
      console.error('[CalendarComponent] loadEvents fetch failed:', error);
      return [];
    }
  }

  private async fetchGridDataRows(
    fromISO: string,
    toISO: string
  ): Promise<DashboardGridRow[]> {
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content ?? '';

    const response = await fetch(this.dataUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        startRow: 0,
        endRow: MAX_EVENTS_FETCH,
        sortModel: [{ colId: 'period', sort: 'asc' }],
        filterModel: {
          ...(this.gridApi?.getFilterModel() ?? {}),
          period: {
            filterType: 'date',
            type: 'inRange',
            dateFrom: fromISO,
            dateTo: toISO,
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`calendar grid_data fetch failed: ${response.status}`);
    }

    const data = (await response.json()) as DashboardGridDataResponse;
    return Array.isArray(data.rows) ? data.rows : [];
  }

  private mapRowToEvent(row: DashboardGridRow): CalendarEvent {
    return {
      id: Number(row.id),
      name: row.name,
      type_of: row.type_of,
      status: row.status_label ?? '',
      start_date: row.start_on as string,
      end_date: (row.end_on as string) ?? (row.start_on as string),
      turnout: this.extractTurnout(row.schedule_summary_html),
    };
  }

  private extractTurnout(html: string | null): string {
    if (!html) return '—';

    const holder = document.createElement('div');
    holder.innerHTML = html;
    holder.querySelectorAll('div, p, li, br').forEach((el) => {
      el.parentNode?.insertBefore(document.createTextNode(' '), el);
    });
    const text = (holder.textContent ?? '').replace(/\s+/g, ' ').trim();
    return text || '—';
  }

  private async renderMonth(): Promise<void> {
    const year = this.cursor.getFullYear();
    const month = this.cursor.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);

    const weeks = this.weeksFor(year, month);
    const gridStart = weeks[0][0];
    const gridEnd = weeks[weeks.length - 1][6];

    const events = await this.loadEvents(
      this.toISO(gridStart),
      this.toISO(gridEnd)
    );

    this.monthTitleTarget.innerHTML = `${MONTH_NAMES[month]} <span class="${this.b('year')}">${year}</span>`;

    this.gridTarget.innerHTML = '';
    weeks.forEach((week) => {
      this.gridTarget.appendChild(this.buildWeekRow(week, month, events));
    });

    this.renderFooter(events, monthStart, monthEnd);
  }

  private toISO(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private weeksFor(year: number, month: number): Date[][] {
    const first = new Date(year, month, 1);
    const start = new Date(year, month, 1 - first.getDay());
    const weeks: Date[][] = [];
    let cursor = start;

    for (let w = 0; w < 6; w += 1) {
      const days: Date[] = [];
      for (let i = 0; i < 7; i += 1) {
        days.push(new Date(cursor));
        cursor = new Date(cursor.getTime() + DAY_MS);
      }
      weeks.push(days);
    }

    return weeks;
  }

  private layoutWeek(weekDays: Date[], events: CalendarEvent[]): WeekSegment[] {
    const weekStart = stripTime(weekDays[0]);
    const weekEnd = stripTime(weekDays[6]);
    const segments: WeekSegment[] = [];

    events.forEach((calendarEvent) => {
      const start = stripTime(parseISODate(calendarEvent.start_date));
      const end = stripTime(parseISODate(calendarEvent.end_date));
      if (end < weekStart || start > weekEnd) return;

      const segStart = start < weekStart ? weekStart : start;
      const segEnd = end > weekEnd ? weekEnd : end;
      const col = Math.round((segStart.getTime() - weekStart.getTime()) / DAY_MS) + 1;
      const colEnd = Math.round((segEnd.getTime() - weekStart.getTime()) / DAY_MS) + 2;

      segments.push({
        event: calendarEvent,
        col,
        colEnd,
        lane: 0,
        contL: start < weekStart,
        contR: end > weekEnd,
        sortKey: start.getTime(),
      });
    });

    segments.sort(
      (a, b) =>
        a.col - b.col ||
        b.colEnd - b.col - (a.colEnd - a.col) ||
        a.sortKey - b.sortKey
    );

    const lanes: number[] = [];
    segments.forEach((segment) => {
      let placed = false;
      for (let lane = 0; lane < lanes.length; lane += 1) {
        if (segment.col >= lanes[lane]) {
          segment.lane = lane;
          lanes[lane] = segment.colEnd;
          placed = true;
          break;
        }
      }
      if (!placed) {
        segment.lane = lanes.length;
        lanes.push(segment.colEnd);
      }
    });

    return segments;
  }

  private buildWeekRow(
    week: Date[],
    month: number,
    events: CalendarEvent[]
  ): HTMLElement {
    const segments = this.layoutWeek(week, events);
    const laneCount = segments.reduce(
      (max, segment) => Math.max(max, segment.lane + 1),
      0
    );

    const visibleLaneCount = Math.min(laneCount, MAX_LANES);
    const hasOverflow = laneCount > MAX_LANES;
    const rowCount = visibleLaneCount + (hasOverflow ? 1 : 0);

    const weekEl = document.createElement('div');
    weekEl.className = this.b('week');
    weekEl.style.gridTemplateRows = `auto repeat(${Math.max(rowCount, 1)}, 2.6rem)`;

    week.forEach((date) => {
      const cell = document.createElement('div');
      const inMonth = date.getMonth() === month;
      const classes = [this.b('day')];
      if (!inMonth) classes.push(this.b('day--dim'));
      if (sameDay(date, this.today)) classes.push(this.b('day--today'));
      cell.className = classes.join(' ');
      cell.innerHTML = `<div class="${this.b('daynum')}">${date.getDate()}</div>`;
      if (inMonth && this.isPlanExpiry(date)) {
        const flag = document.createElement('span');
        flag.className = this.b('plan-flag');
        flag.textContent = 'Plan ends';
        flag.title = `Plan ends ${formatDate(date)}`;
        cell.appendChild(flag);
      }
      weekEl.appendChild(cell);
    });

    segments
      .filter((segment) => segment.lane < MAX_LANES)
      .forEach((segment) => {
        weekEl.appendChild(this.buildEventBar(segment));
      });

    if (hasOverflow) {
      const overflowByCol = new Map<number, number>();
      segments
        .filter((segment) => segment.lane >= MAX_LANES)
        .forEach((segment) => {
          for (let col = segment.col; col < segment.colEnd; col += 1) {
            overflowByCol.set(col, (overflowByCol.get(col) ?? 0) + 1);
          }
        });

      overflowByCol.forEach((count, col) => {
        weekEl.appendChild(
          this.buildMorePill(col, visibleLaneCount + 2, week[col - 1], events)
        );
      });
    }

    return weekEl;
  }

  private buildMorePill(
    col: number,
    row: number,
    date: Date,
    events: CalendarEvent[]
  ): HTMLButtonElement {
    const dayEvents = this.eventsOnDay(date, events);

    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = this.b('more');
    pill.style.gridColumn = `${col} / ${col + 1}`;
    pill.style.gridRow = `${row}`;
    pill.textContent = `+${dayEvents.length - MAX_LANES} more`;
    pill.title = `${dayEvents.length} elections on ${formatDate(date)}`;
    pill.addEventListener('click', () => this.openDayAgenda(date, dayEvents));

    return pill;
  }

  private eventsOnDay(date: Date, events: CalendarEvent[]): CalendarEvent[] {
    const day = stripTime(date);
    return events
      .filter((calendarEvent) => {
        const start = stripTime(parseISODate(calendarEvent.start_date));
        const end = stripTime(parseISODate(calendarEvent.end_date));
        return start <= day && day <= end;
      })
      .sort(
        (a, b) =>
          parseISODate(a.start_date).getTime() -
            parseISODate(b.start_date).getTime() ||
          LEGEND_ORDER.indexOf(a.status) - LEGEND_ORDER.indexOf(b.status) ||
          a.name.localeCompare(b.name)
      );
  }

  private buildEventBar(segment: WeekSegment): HTMLButtonElement {
    const { event: calendarEvent } = segment;
    const bar = document.createElement('button');
    bar.type = 'button';

    const classes = [this.b('evt')];
    if (segment.contL) classes.push(this.b('evt--cont-l'));
    if (segment.contR) classes.push(this.b('evt--cont-r'));
    bar.className = classes.join(' ');

    bar.style.setProperty(
      '--cal-c',
      `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`
    );
    bar.style.gridColumn = `${segment.col} / ${segment.colEnd}`;
    bar.style.gridRow = `${segment.lane + 2}`;
    bar.title = calendarEvent.name;

    const tag = document.createElement('span');
    tag.className = this.b('evt-tag');

    const name = document.createElement('span');
    name.className = this.b('evt-name');
    name.textContent = calendarEvent.name;

    bar.append(tag, name);

    if (segment.contR) {
      const arrow = document.createElement('span');
      arrow.className = this.b('evt-arrow');
      arrow.textContent = '›';
      bar.appendChild(arrow);
    }

    bar.addEventListener('click', () => this.openDetail(calendarEvent));

    return bar;
  }

  private renderFooter(
    events: CalendarEvent[],
    monthStart: Date,
    monthEnd: Date
  ): void {
    const scheduledCount = events.filter((calendarEvent) => {
      const start = parseISODate(calendarEvent.start_date);
      const end = parseISODate(calendarEvent.end_date);
      return !(end < monthStart || start > monthEnd);
    }).length;

    if (this.loadFailed) {
      this.scheduledCountTarget.textContent = '';
      this.unscheduledNoteTarget.textContent =
        'Could not load your elections. Refresh to try again.';
      return;
    }

    const expiry = this.planExpiryDate();
    const planNote = expiry ? `Plan ends ${formatDate(expiry)} · ` : '';
    this.scheduledCountTarget.textContent = `${planNote}${scheduledCount} scheduled this month`;

    if (this.unscheduledCountValue <= 0) {
      this.unscheduledNoteTarget.textContent = '';
      return;
    }

    const plural = this.unscheduledCountValue === 1 ? '' : 's';
    this.unscheduledNoteTarget.innerHTML =
      `<b>${this.unscheduledCountValue}</b> unscheduled election${plural} ` +
      'have no dates — <a class="' +
      this.b('link') +
      '" href="#">see them in the grid</a>.';
  }

  private buildWeekdayHeader(): void {
    this.weekdaysTarget.innerHTML = WEEKDAY_NAMES.map(
      (day) => `<div>${day}</div>`
    ).join('');
  }

  private buildLegend(): void {
    this.legendTarget.innerHTML = LEGEND_ORDER.map(
      (status) =>
        `<span><i class="${this.b('dot')}" style="background: var(${STATUS_COLOR_VAR[status]})"></i>${status}</span>`
    ).join('');
  }

  private openDetail(calendarEvent: CalendarEvent): void {
    this.detailNameTarget.textContent = calendarEvent.name;
    this.detailTypeTarget.textContent =
      TYPE_LABEL[calendarEvent.type_of] ?? calendarEvent.type_of;

    this.detailStatusTarget.textContent = calendarEvent.status;
    this.detailStatusTarget.style.background = `var(${STATUS_COLOR_VAR[calendarEvent.status] ?? DEFAULT_STATUS_COLOR_VAR})`;

    this.detailDatesTarget.textContent = formatDateRange(
      parseISODate(calendarEvent.start_date),
      parseISODate(calendarEvent.end_date)
    );
    this.detailTurnoutTarget.textContent = calendarEvent.turnout;

    this.detailOpenLinkTarget.href = `/elections/${calendarEvent.id}/edit`;

    this.detailTarget.setAttribute('open', '');
  }

  private b(name: string): string {
    return `${UNIQUE_ID}__${name}`;
  }
}
